export type Transaction = { 
  from: string; 
  fromAvatarUrl?: string | null;
  to: string; 
  toAvatarUrl?: string | null;
  amount: number 
};

/**
 * Returns the minimum set of cash transfers needed to clear all debts.
 * Uses a greedy matching (debt-minimization) algorithm: repeatedly match 
 * the largest debtor against the largest creditor until all balances reach zero.
 * 
 * In professional accounting terms, debtors have a negative Net Position
 * and creditors have a positive Net Position.
 */
export function minimiseTransactions(
  totals: { name: string; net: number; avatar_url?: string | null }[]
): Transaction[] {
  // 1. Ensure all inputs are rounded to 2 decimal places to avoid float precision issues
  const roundedTotals = totals.map(t => ({
    ...t,
    net: Math.round(t.net * 100) / 100
  }));

  // 2. Zero-sum invariant check: In any settlement, the total net must be zero.
  // If it's not (due to rounding differences in expense splitting), we adjust the 
  // largest balance to ensure the invariant holds.
  const totalSum = roundedTotals.reduce((sum, t) => sum + t.net, 0);
  if (Math.abs(totalSum) > 0.001) {
    // Find the player with the largest absolute balance to absorb the discrepancy
    let maxIdx = 0;
    let maxAbs = -1;
    for (let i = 0; i < roundedTotals.length; i++) {
      const abs = Math.abs(roundedTotals[i].net);
      if (abs > maxAbs) {
        maxAbs = abs;
        maxIdx = i;
      }
    }
    
    if (roundedTotals.length > 0) {
      console.warn(`Settlement zero-sum invariant check failed (diff: ${totalSum}). Adjusting ${roundedTotals[maxIdx].name}.`);
      roundedTotals[maxIdx].net = Math.round((roundedTotals[maxIdx].net - totalSum) * 100) / 100;
    }
  }

  const eps = 0.005; // half a cent — absorbs float drift

  const debtors = roundedTotals
    .filter((p) => p.net < -eps)
    .map((p) => ({ name: p.name, net: p.net, avatar_url: p.avatar_url }))
    .sort((a, b) => a.net - b.net); // most negative first (largest debtor)

  const creditors = roundedTotals
    .filter((p) => p.net > eps)
    .map((p) => ({ name: p.name, net: p.net, avatar_url: p.avatar_url }))
    .sort((a, b) => b.net - a.net); // most positive first (largest creditor)

  const txns: Transaction[] = [];
  let d = 0;
  let c = 0;

  while (d < debtors.length && c < creditors.length) {
    const pay = Math.min(-debtors[d].net, creditors[c].net);
    txns.push({
      from: debtors[d].name,
      fromAvatarUrl: debtors[d].avatar_url,
      to: creditors[c].name,
      toAvatarUrl: creditors[c].avatar_url,
      amount: Math.round(pay * 100) / 100,
    });
    debtors[d].net += pay;
    creditors[c].net -= pay;
    if (Math.abs(debtors[d].net) < eps) d++;
    if (Math.abs(creditors[c].net) < eps) c++;
  }

  return txns;
}

/**
 * Calculates the net impact of expenses on each player.
 * Payer gets credited the full amount, and each participant is debited their share.
 */
export function calculateExpenseNets(
  expenses: any[],
  keySelector: (exp: any, type: 'payer' | 'participant', participant?: any) => string = (exp, type, part) => 
    type === 'payer' ? exp.payer_id : part.player_id
): Record<string, number> {
  const nets: Record<string, number> = {};

  expenses.forEach((exp) => {
    const amount = Number(exp.amount || 0);
    const payerKey = keySelector(exp, 'payer');

    nets[payerKey] = Math.round(((nets[payerKey] || 0) + amount) * 100) / 100;

    exp.participants?.forEach((p: any) => {
      const participantKey = keySelector(exp, 'participant', p);
      const share = Number(p.share || 0);
      nets[participantKey] = Math.round(((nets[participantKey] || 0) - share) * 100) / 100;
    });
  });

  return nets;
}

export function calculateExpensePaidMap(
  expenses: any[],
  keySelector: (exp: any) => string = (exp) => exp.payer_id
): Record<string, number> {
  const paidMap: Record<string, number> = {};
  expenses.forEach((exp) => {
    const key = keySelector(exp);
    const amount = Number(exp.amount || 0);
    paidMap[key] = Math.round(((paidMap[key] || 0) + amount) * 100) / 100;
  });
  return paidMap;
}

export function calculateExpenseShareMap(
  expenses: any[],
  keySelector: (participant: any) => string = (part) => part.player_id
): Record<string, number> {
  const shareMap: Record<string, number> = {};
  expenses.forEach((exp) => {
    exp.participants?.forEach((p: any) => {
      const key = keySelector(p);
      const share = Number(p.share || 0);
      shareMap[key] = Math.round(((shareMap[key] || 0) + share) * 100) / 100;
    });
  });
  return shareMap;
}

/**
 * Professional Accounting Standard Balance Calculation
 * Total Outlay (A) = Cash Out + Expense Paid
 * Cost Share (B) = (Buy-in * Unit) + Expense Share
 * Net Position = A - B
 */
export function calculatePlayerBalance(
  buyIns: number,
  buyInUnit: number,
  cashOut: number,
  expensePaid: number,
  expenseShare: number
) {
  const gameNet = Math.round((cashOut - (buyIns * buyInUnit)) * 100) / 100;
  const outlayA = Math.round((cashOut + expensePaid) * 100) / 100;
  const shareB = Math.round(((buyIns * buyInUnit) + expenseShare) * 100) / 100;
  const netPosition = Math.round((outlayA - shareB) * 100) / 100;
  
  return {
    gameNet,
    outlayA,
    shareB,
    netPosition
  };
}
